import { faArrowLeft, faClock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";

import type { Theme } from "@mui/system";
import {
    LoadingWrapper,
    VaporPageStickable,
    type ComicDrawerProps,
} from "@vapor/react-contrib-tax-compliance";
import Typography from "@vapor/react-extended/ExtendedTypography";
import {
    Autocomplete,
    Box,
    Divider,
    IconButton,
    Stack,
    TextField,
    useMediaQuery,
    useTheme,
} from "@vapor/react-material";
import dayjs from "dayjs";
import queryString from "query-string";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";
import { useCloseFulfilment } from "../../../core/api/use-close-fulfilment/useCloseFulfilment";
import type { FulfilmentActivityDto } from "../../../core/api/use-get-fulfilment-activities/FulfilmentActivityDto";
import { FulfilmentStatus } from "../../../core/api/use-get-fulfilment-detail/FulfilmentStatus";
import { useGetFulfilmentDetail } from "../../../core/api/use-get-fulfilment-detail/useGetFulfilmentDetail";
import type { TaxPayerDetailsDto } from "../../../core/api/use-get-fulfilment-tax-payers/TaxPayerDetailsDto";
import { useGetFulfilmentTaxPayers } from "../../../core/api/use-get-fulfilment-tax-payers/useGetFulfilmentTaxPayers";
import { useReopenFulfilment } from "../../../core/api/use-reopen-fulfilment/useReopenFulfilment";
import { useUnlinkTaxPayerFromFulfilment } from "../../../core/api/use-unlink-tax-payer-from-fulfilment/useUnlinkTaxPayerFromFulfilment";
import routes from "../../../core/commons/routes";
import { ActivityStatus } from "../../../core/dtos/ActivityStatus";
import { useSuccessNotification } from "../../../core/hooks/useSuccessNotification";
import { fulfilmentSearchFiltersStoreSlice } from "../../../store/fulfilmentSearchFiltersStoreSlice";
import { sessionStoreSlice } from "../../../store/sessionStoreSlice";
import { formatDate } from "../../../utils/dateUtils";
import { createNavigationUrl } from "../../../utils/resolveUrn";
import { getTaxPayerIdentifier } from "../../../utils/taxPayer";
import { ActivityCounter } from "../../components/activity-counter/ActivityCounter";
import { AddActivity } from "./components/add-activity/AddActivity";
import { FulfimentActivityEdit } from "./components/fulfilment-activity-edit/FulfilmentActivityEdit";
import { FulfimentActivityNote } from "./components/fulfilment-activity-note/FulfilmentActivityNote";
import { FulfilmentActivityTimeline } from "./components/fulfilment-activity-timeline/FulfilmentActivityTimeline";
import { FulfilmentDetailFooter } from "./components/fulfilment-detail-footer/FulfilmentDetailFooter";
import { FulfilmentPdfPreview } from "./components/fulfilment-pdf-preview/FulfilmentPdfPreview";
import { FulfilmentDetailCTAOptions } from "./FulfilmentDetailCTAOptions";

const defaultFulfilmentDataParams = {
    taxPayerWorkspaceId: "",
    levyId: "",
    year: Number(dayjs().year().toString()),
};

interface FulfilmentDataParams {
    taxPayerWorkspaceId: string;
    levyId: string;
    year: number;
}

export const FulfilmentDetail = () => {
    const { t } = useTranslation();
    const { showTextMessage } = useSuccessNotification();
    const activitiesWidth = document.getElementById(
        "fulfilmentActivitiesContainer",
    )?.clientWidth;
    const isXlScreen = useMediaQuery((theme: Theme) =>
        theme.breakpoints.up(theme.breakpoints.values.xl),
    );

    // states
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();

    const [drawerContent, setDrawerContent] = useState<ComicDrawerProps>();
    const [openDrawer, setOpenDrawer] = useState(false);

    const fulfilmentData: FulfilmentDataParams = useMemo(() => {
        const queryParams = queryString.parse(
            location.search,
        ) as unknown as FulfilmentDataParams;
        if (
            queryParams &&
            queryParams.taxPayerWorkspaceId &&
            queryParams.levyId &&
            queryParams.year
        ) {
            return {
                taxPayerWorkspaceId: queryParams.taxPayerWorkspaceId,
                levyId: queryParams.levyId,
                year: Number(queryParams.year),
            };
        }
        return defaultFulfilmentDataParams;
    }, [location.search]);

    // use cases — declarative TanStack Query hooks
    const {
        data: fulfilmentDetail,
        isFetching: loadingGetFulfilmentDetail,
        isError: errorGetFulfilmentDetail,
    } = useGetFulfilmentDetail(fulfilmentData);

    const {
        data: fulfilmentTaxPayers,
        isFetching: loadingFulfilmentTaxPayers,
    } = useGetFulfilmentTaxPayers({
        levyId: fulfilmentData.levyId,
        year: fulfilmentData.year,
    });

    const closeFulfilmentMutation = useCloseFulfilment();
    const reopenFulfilmentMutation = useReopenFulfilment();
    const unlinkTaxPayerMutation = useUnlinkTaxPayerFromFulfilment();

    const isFulfilmentCloseable = () =>
        fulfilmentDetail?.status === FulfilmentStatus.PENDING ||
        fulfilmentDetail?.status === FulfilmentStatus.CLOSED;

    const isFulfilmentReopenable = () =>
        fulfilmentDetail ? fulfilmentDetail.isReopenable : false;

    const isTaxPayerDissociable = () => {
        return fulfilmentDetail?.status === FulfilmentStatus.LINKED;
    };

    const selectedTaxPayer = useMemo(() => {
        if (!fulfilmentTaxPayers || !fulfilmentDetail) return null;

        return (
            fulfilmentTaxPayers.find((taxPayer) => {
                return (
                    taxPayer.workspaceId ===
                    fulfilmentDetail.taxPayer.workspaceId
                );
            }) || null
        );
    }, [fulfilmentTaxPayers, fulfilmentDetail]);

    const daysLeft = useMemo(() => {
        if (!fulfilmentDetail) return 0;

        return dayjs(fulfilmentDetail.deadline).diff(dayjs(), "day");
    }, [fulfilmentDetail]);

    useEffect(() => {
        if (errorGetFulfilmentDetail) {
            // TODO: handle business error with 404 not found
            // window.location.href = "/404";
            sessionStoreSlice.updateValues({ selectedFulfilment: undefined });
            navigate(routes.home());
        }
    }, [errorGetFulfilmentDetail, navigate]);

    // functions
    async function handleClickCloseFulfilment() {
        try {
            await closeFulfilmentMutation.mutateAsync({
                taxPayerWorkspaceId: fulfilmentData.taxPayerWorkspaceId,
                levyId: fulfilmentData.levyId,
                year: Number(fulfilmentData.year),
            });
        } catch {
            return;
        }
        showTextMessage(
            t("views.fulfilment_detail.actions.close_fulfilment.successfull"),
        );
    }

    async function handleClickReopenFulfilment() {
        try {
            await reopenFulfilmentMutation.mutateAsync({
                taxPayerWorkspaceId: fulfilmentData.taxPayerWorkspaceId,
                levyId: fulfilmentData.levyId,
                year: Number(fulfilmentData.year),
            });
        } catch {
            return;
        }
        showTextMessage(
            t("views.fulfilment_detail.actions.reopen_fulfilment.successfull"),
        );
    }

    async function handleClickUnlinkTaxPayer() {
        // When dissociating the last associated tax payer, the fulfilment
        // ceases to exist server-side (a fulfilment exists only while it has
        // at least one associated tax payer, in any status). In that case we
        // must clear the selected fulfilment so the home drawer does not
        // reopen on a fulfilment that no longer exists (which would 404 on
        // its tax-payers list and leave stale entries clickable).
        const isLastTaxPayer = fulfilmentTaxPayers.length <= 1;

        try {
            await unlinkTaxPayerMutation.mutateAsync({
                taxPayerWorkspaceId: fulfilmentData.taxPayerWorkspaceId,
                levyId: fulfilmentData.levyId,
                year: Number(fulfilmentData.year),
            });
            showTextMessage(
                t(
                    "views.fulfilment_detail.actions.unlink_taxpayer.successfull",
                ),
            );
        } catch {
            return;
        }

        if (isLastTaxPayer) {
            sessionStoreSlice.updateValues({ selectedFulfilment: undefined });
        }

        navigate(routes.home());
    }

    const handleSelectedTaxPayerChange = useCallback(
        (_: unknown, value: TaxPayerDetailsDto | null) => {
            if (value)
                navigate({
                    pathname: routes.fulfilmentDetail(),
                    search: createSearchParams({
                        taxPayerWorkspaceId: value.workspaceId,
                        levyId: value.levyId,
                        year: dayjs(value.deadline).year().toString(),
                    }).toString(),
                });
        },
        [navigate],
    );

    const onClickCloseDrawer = () => {
        setDrawerContent(undefined);
        setOpenDrawer(false);
    };

    const handleOnClickGoToTaxPayerFulfilments = () => {
        fulfilmentSearchFiltersStoreSlice.updateValues({
            taxPayerWorkspaceId: fulfilmentData.taxPayerWorkspaceId,
        });
        sessionStoreSlice.updateValues({ selectedFulfilment: undefined });
        navigate(routes.home());
    };

    const handleOnClickPreviewModel = () => {
        if (!fulfilmentDetail || !fulfilmentDetail.sourceDocumentId) return;

        setOpenDrawer(true);
        setDrawerContent({
            content: (
                <FulfilmentPdfPreview
                    taxPayerWorkspaceId={fulfilmentDetail.taxPayer.workspaceId}
                    taxPayerTaxId={fulfilmentDetail.taxPayer.taxId}
                    sourceDocumentId={fulfilmentDetail.sourceDocumentId}
                />
            ),
            headerTitle: t("views.fulfilment_detail.drawer.title.preview"),
            onClickCloseButton: onClickCloseDrawer,
            open: true,
            sxContent: { padding: 0 },
            width: isXlScreen ? "760px" : "40%",
        });
    };

    const handleOnClickAddActivity = () => {
        setOpenDrawer(true);
        setDrawerContent({
            content: (
                <AddActivity
                    onAddActivity={onUpdateActivity}
                    onClickCancel={onClickCloseDrawer}
                    levyId={fulfilmentData.levyId}
                    taxPayerWorkspaceId={fulfilmentData.taxPayerWorkspaceId}
                    year={Number(fulfilmentData.year)}
                />
            ),
            headerTitle: t(
                "views.fulfilment_detail.drawer.title.add_new_activity",
            ),
            onClickCloseButton: onClickCloseDrawer,
            open: true,
            sxContent: { padding: 0 },
            width: isXlScreen ? "30%" : "40%",
        });
    };

    const canExecuteJump = () => {
        return (
            fulfilmentDetail?.activities?.[0]?.ownerApp?.appName &&
            fulfilmentDetail?.activities?.[0]?.ownerApp?.taxPayerPath
        );
    };

    const onClickActivityEditTitle = (activity: FulfilmentActivityDto) => {
        setOpenDrawer(true);
        setDrawerContent({
            content: (
                <FulfimentActivityEdit
                    activity={activity}
                    levyId={fulfilmentData.levyId}
                    onClickCancel={onClickCloseDrawer}
                    onUpdateTitle={onUpdateActivity}
                    taxPayerWorkspaceId={fulfilmentData.taxPayerWorkspaceId}
                    year={Number(fulfilmentData.year)}
                />
            ),
            headerTitle: t(
                "views.home.drawer.fulfilment_detail.edit_activity_title.header",
            ),
            onClickCloseButton: onClickCloseDrawer,
            open: true,
            sxContent: { padding: 0 },
            width: isXlScreen ? "30%" : "40%",
        });
    };

    const onClickActivityViewNote = (activity: FulfilmentActivityDto) => {
        setOpenDrawer(true);
        setDrawerContent({
            content: (
                <FulfimentActivityNote
                    activity={activity}
                    fulfilmentStatus={fulfilmentDetail?.status}
                    levyId={fulfilmentData.levyId}
                    onUpdateNote={onUpdateActivity}
                    taxPayerWorkspaceId={fulfilmentData.taxPayerWorkspaceId}
                    year={Number(fulfilmentData.year)}
                />
            ),
            headerTitle: activity.title,
            onClickCloseButton: onClickCloseDrawer,
            open: true,
            sxContent: { padding: 0 },
            width: isXlScreen ? "30%" : "40%",
        });
    };

    const onUpdateActivity = async () => {
        onClickCloseDrawer();
    };

    return (
        <>
            {!fulfilmentDetail && !loadingGetFulfilmentDetail && (
                <VaporPageStickable></VaporPageStickable>
            )}
            {(fulfilmentDetail || loadingGetFulfilmentDetail) && (
                <VaporPageStickable
                    sxContent={{
                        p: 3,
                    }}
                    footer={
                        <FulfilmentDetailFooter
                            isFulfilmentCloseable={
                                !loadingGetFulfilmentDetail &&
                                isFulfilmentCloseable()
                            }
                            isFulfilmentReopenable={
                                !loadingGetFulfilmentDetail &&
                                isFulfilmentReopenable()
                            }
                            isTaxPayerDissociable={
                                !loadingGetFulfilmentDetail &&
                                isTaxPayerDissociable()
                            }
                            loading={
                                loadingGetFulfilmentDetail ||
                                closeFulfilmentMutation.isPending ||
                                reopenFulfilmentMutation.isPending ||
                                unlinkTaxPayerMutation.isPending
                            }
                            onClickCloseFulfilmentButton={
                                handleClickCloseFulfilment
                            }
                            onClickReopenFulfilmentButton={
                                handleClickReopenFulfilment
                            }
                            onClickUnlinkTaxPayerButton={
                                handleClickUnlinkTaxPayer
                            }
                        />
                    }
                    footerDivider
                    headerBottom={
                        <Stack
                            direction="row"
                            spacing={4}
                            p={3}
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={10}
                            >
                                <LoadingWrapper
                                    loading={loadingGetFulfilmentDetail}
                                    skeletonProps={{
                                        rowsNumber: 1,
                                        sx: { height: 32 },
                                        width: 200,
                                    }}
                                    variant="skeleton-list"
                                >
                                    {fulfilmentDetail && (
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            gap={3}
                                        >
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                gap={1}
                                                sx={{
                                                    background:
                                                        theme.palette.primary
                                                            .background,
                                                    p: 1,
                                                    borderRadius: "4px",
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faClock}
                                                    color={
                                                        theme.palette.primary
                                                            .textTitleColor
                                                    }
                                                />
                                                <Typography
                                                    variant="subtitle2"
                                                    color={
                                                        theme.palette.primary
                                                            .textTitleColor
                                                    }
                                                    sx={{ lineHeight: 1 }}
                                                >
                                                    {daysLeft > 0
                                                        ? t(
                                                              daysLeft === 1
                                                                  ? "views.fulfilment_detail.components.fulfilment_detail.deadline.day_left"
                                                                  : "views.fulfilment_detail.components.fulfilment_detail.deadline",
                                                              {
                                                                  date: formatDate(
                                                                      fulfilmentDetail.deadline,
                                                                  ),
                                                                  daysLeft,
                                                              },
                                                          )
                                                        : t(
                                                              "views.fulfilment_detail.components.fulfilment_detail.expired",
                                                              {
                                                                  date: formatDate(
                                                                      fulfilmentDetail.deadline,
                                                                  ),
                                                              },
                                                          )}
                                                </Typography>
                                            </Stack>
                                            <ActivityCounter
                                                status={
                                                    ActivityStatus[
                                                        fulfilmentDetail?.status as keyof typeof ActivityStatus
                                                    ]
                                                }
                                            />
                                        </Stack>
                                    )}
                                </LoadingWrapper>
                            </Stack>
                            {selectedTaxPayer && (
                                <Autocomplete
                                    value={selectedTaxPayer || undefined}
                                    disabled={loadingFulfilmentTaxPayers}
                                    loading={loadingFulfilmentTaxPayers}
                                    options={fulfilmentTaxPayers || []}
                                    getOptionLabel={(
                                        option: TaxPayerDetailsDto,
                                    ) => getTaxPayerIdentifier(option)}
                                    isOptionEqualToValue={(option, value) => {
                                        return (
                                            option.workspaceId ===
                                            value.workspaceId
                                        );
                                    }}
                                    disableClearable
                                    onChange={handleSelectedTaxPayerChange}
                                    sx={{ ml: "auto", width: 210 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={t(
                                                "views.fulfilment_detail.components.fulfilment_detail.taxPayerSelect.placeholder",
                                            )}
                                        />
                                    )}
                                />
                            )}
                        </Stack>
                    }
                    headerBottomDivider
                    headerLeft={
                        <Stack direction="row" spacing={2} alignItems="center">
                            <IconButton
                                edge="end"
                                onClick={() => {
                                    navigate(routes.home());
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faArrowLeft}
                                    color={
                                        theme.palette.primary.interactiveDefault
                                    }
                                />
                            </IconButton>
                            <LoadingWrapper
                                loading={loadingGetFulfilmentDetail}
                                variant="skeleton-list"
                                skeletonProps={{ rowsNumber: 1, width: 200 }}
                            >
                                {fulfilmentDetail && (
                                    <Stack direction="column">
                                        <Typography
                                            variant="titleSmall"
                                            color={
                                                theme.palette.primary
                                                    .textTitleColor
                                            }
                                        >
                                            {fulfilmentDetail.description}
                                        </Typography>
                                        <Typography
                                            color={
                                                theme.palette.primary
                                                    .textSubduedColor
                                            }
                                            variant="caption"
                                        >
                                            {`${t(
                                                "views.fulfilment_detail.components.fulfilment_detail.fulfilment_type",
                                            )}: ${fulfilmentDetail.title}`}
                                        </Typography>
                                    </Stack>
                                )}
                            </LoadingWrapper>
                        </Stack>
                    }
                    stickyHeader
                    stickyFooter
                >
                    <Stack direction="row" justifyContent="space-between">
                        <Stack
                            direction="column"
                            spacing={2}
                            alignItems="start"
                        >
                            <Box>
                                <Typography
                                    variant="titleMedium"
                                    color={theme.palette.text.primary}
                                >
                                    {t(
                                        "views.fulfilment_detail.components.fulfilment_detail.activity",
                                    )}
                                </Typography>
                            </Box>
                            <Stack direction="row" justifyContent="center">
                                <Stack
                                    id="fulfilmentActivitiesContainer"
                                    width={"50vw"}
                                    marginRight={5}
                                >
                                    {fulfilmentDetail &&
                                        fulfilmentDetail.status && (
                                            <FulfilmentActivityTimeline
                                                fulfilmentStatus={
                                                    fulfilmentDetail.status
                                                }
                                                levyId={fulfilmentData.levyId}
                                                onClickActivityEditTitle={
                                                    onClickActivityEditTitle
                                                }
                                                onClickActivityViewNote={
                                                    onClickActivityViewNote
                                                }
                                                onUpdateActivity={
                                                    onUpdateActivity
                                                }
                                                taxPayerWorkspaceId={
                                                    fulfilmentData.taxPayerWorkspaceId
                                                }
                                                year={Number(
                                                    fulfilmentData.year,
                                                )}
                                            />
                                        )}
                                </Stack>
                                <Divider
                                    orientation="vertical"
                                    flexItem
                                    sx={{ borderBottomWidth: 5 }}
                                />
                                <Stack
                                    width={`calc(95vw-${activitiesWidth}px)`}
                                >
                                    <FulfilmentDetailCTAOptions
                                        fulfilmentStatus={
                                            fulfilmentDetail?.status
                                        }
                                        handleOnClickGoToTaxPayerFulfilments={
                                            handleOnClickGoToTaxPayerFulfilments
                                        }
                                        handleOnClickAddActivity={
                                            handleOnClickAddActivity
                                        }
                                        handleOnClickPreviewModel={
                                            fulfilmentDetail &&
                                            fulfilmentDetail.sourceDocumentId
                                                ? handleOnClickPreviewModel
                                                : undefined
                                        }
                                        handleOnClickGoToProcedure={
                                            canExecuteJump()
                                                ? () =>
                                                      navigate(
                                                          createNavigationUrl(
                                                              fulfilmentDetail!
                                                                  .activities[0]
                                                                  .ownerApp
                                                                  .taxPayerPath!,
                                                          ),
                                                      )
                                                : undefined
                                        }
                                    ></FulfilmentDetailCTAOptions>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Stack>
                </VaporPageStickable>
            )}
            {openDrawer && (
                <VaporPageStickable drawer={drawerContent}></VaporPageStickable>
            )}
        </>
    );
};
